import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Menu from '~/shared/components/Menu';
import MenuButton from '~/shared/components/MenuButton';
import withRouter, {routerPropTypes} from '~/router/withRouter';

import './index.scss';

const noop = () => {};

const propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  router: routerPropTypes,
  onItemClick: PropTypes.func,
  onMenuToggle: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
class MainMenuSidebar extends Component {
  static displayName = 'MainMenuSidebar';
  static propTypes = propTypes;

  static defaultProps = {
    onItemClick: noop,
    onMenuToggle: noop
  };

  state = {
    mainMenuWidth: 0,
    mountMenu: false,
    revealMenu: false
  };

  resolveTogglePromise = noop;

  toggleMenu = () => {
    this.props.onMenuToggle(!this.state.revealMenu || this.mountMenu);
    this.state.revealMenu
      ? this.setState({revealMenu: false})
      : this.setState({mountMenu: true});
    return new Promise(resolve => (this.resolveTogglePromise = resolve))
      .then(() => (this.resolveTogglePromise = noop))
      .catch(() => (this.resolveTogglePromise = noop));
  };

  handleMenuMount = mainMenuNode => {
    const mainMenuWidth =
      mainMenuNode && mainMenuNode.getBoundingClientRect().width;
    if (mainMenuWidth) {
      this.setState({
        mainMenuWidth,
        revealMenu: true
      });
    }
  };

  handleItemClick = (type, path, label) => {
    this.toggleMenu().then(() => {
      this.props.onItemClick(String(label).toLowerCase());
      switch (type) {
        case 'navigate':
          this.props.router.navigate(path);
          break;
        case 'redirect':
          window.location.assign(path);
          break;
      }
    });
  };

  handleChildClick = evt => {
    if (this.state.revealMenu) {
      evt.stopPropagation();
      this.toggleMenu();
    }
  };

  handleSuppressedEvent = evt => {
    if (this.state.mountMenu) {
      evt.stopPropagation();
    }
  };

  handleChildTransitionEnd = () => {
    this.setState({
      mountMenu: this.state.revealMenu
    });
    this.resolveTogglePromise();
  };

  getMenuItems() {
    return [
      {
        handlerType: 'redirect',
        path: '/accounts',
        text: 'Your Account'
      },
      {
        handlerType: 'navigate',
        path: '/productCatalog',
        text: 'Products'
      },
      {
        handlerType: 'redirect',
        path: '/inspiration',
        text: 'Design Templates'
      },
      {
        handlerType: 'redirect',
        path: '/about',
        text: 'About Us'
      },
      {
        handlerType: 'navigate',
        path: '/help',
        text: 'Help'
      }
    ];
  }

  renderMenu() {
    const menuItemClassName = 'ndx-MainMenuSidebar-Item';
    const menuItems = this.getMenuItems();
    return (
      <Menu className="ndx-MainMenuSidebar" domRef={this.handleMenuMount}>
        <span className="ndx-MainMenuSidebar-label">Menu</span>
        {menuItems.map(item => {
          return (
            <MenuButton
              key={item.text}
              className={menuItemClassName}
              onClick={() =>
                this.handleItemClick(item.handlerType, item.path, item.text)
              }
              {...item}
            />
          );
        })}
      </Menu>
    );
  }

  render() {
    const {mainMenuWidth, mountMenu, revealMenu} = this.state;
    const className = classnames(
      'ndx-MainMenuSidebar-container',
      this.props.className,
      {'ndx-MainMenuSidebar--open': mountMenu}
    );
    const childComponent = React.Children.only(this.props.children);
    const childProps = {
      ...childComponent.props,
      onClickCapture: this.handleSuppressedEvent,
      onMouseDownCapture: this.handleSuppressedEvent,
      onMouseUpCapture: this.handleChildClick,
      onTouchEndCapture: this.handleChildClick,
      onTouchMoveCapture: this.handleSuppressedEvent,
      onTouchStartCapture: this.handleSuppressedEvent,
      onTransitionEnd: this.handleChildTransitionEnd,
      style: {
        ...(childComponent.props.style || {}),
        transition: 'transform 200ms'
      }
    };
    if (revealMenu && mainMenuWidth) {
      childProps.style = {
        ...childProps.style,
        transform: `translate3d(${mainMenuWidth}px, 0, 0)`
      };
    }
    return (
      <div className={className}>
        {mountMenu && this.renderMenu()}
        {React.cloneElement(childComponent, childProps)}
      </div>
    );
  }
}

export default withRouter(MainMenuSidebar);
